import React, { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "./index.scss";

import CreateExpenseComponent from "@components/createExpenseComponent/CreateExpenseComponent";
import CustomDialog from "@components/CustomDialog/CustomDialog";
import CustomHeader from "@components/CustomHeader/CustomHeader";
import CustomTable from "@components/CustomTable/CustomTable";
import Loader from "@components/Loader/Loader";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import IconButton from "@mui/material/IconButton";
import { CreateModalContext } from "layouts/appLayout/layout";
import ReactPaginate from "react-paginate"; // ✅ import pagination
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  useDeleteExpenseMutation,
  useGetExpensesQuery,
} from "store/expense/expense.Api";

const ExpensesPage = () => {
  const { t } = useTranslation();
  const { toggleCreateModal } = useContext(CreateModalContext);

  const currentUser = useSelector((state) => state.auth.user);
  const companyId = currentUser?.companyId?._id;

  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const { data, isLoading, refetch } = useGetExpensesQuery({ companyId });
  const [deleteExpense, { isLoading: isDeletingLoading }] =
    useDeleteExpenseMutation();

  const expenses = data?.data || [];

  // ✅ Pagination states
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 5; // change si tu veux plus/moins d’éléments par page

  // ✅ Permissions
  const canEditExpense =
    currentUser?.roleId?.permissions.includes("EDIT_EXPENSE");
  const canDeleteExpense =
    currentUser?.roleId?.permissions.includes("DELETE_EXPENSE");
  const canViewExpense =
    currentUser?.roleId?.permissions.includes("VIEW_EXPENSE");

  const headers = [
    { key: "dateCreationExpense", label: t("depense.form.dateCreation") },

    { key: "invoiceNumber", label: t("depense.table.invoice_number") },
    { key: "createdBy", label: t("depense.table.createdBy") },
    { key: "supplierId", label: t("depense.form.supplier") },
    { key: "date", label: t("depense.form.date") },
    { key: "totalTTC", label: t("depense.total.ttc") },
  ];

  const renderCell = (key, expense) => {
    if (key === "supplierId") {
      let supplierName = "—";
      if (expense?.supplierId?.name) {
        supplierName = expense.supplierId.name;
      } else if (expense?.supplierInfo) {
        try {
          const supplierInfo =
            typeof expense.supplierInfo === "string"
              ? JSON.parse(expense.supplierInfo)
              : expense.supplierInfo;
          if (supplierInfo?.name) supplierName = supplierInfo.name;
        } catch (error) {
          console.error("Error parsing supplierInfo:", error);
        }
      }
      return supplierName;
    }
    if (key === "date") return new Date(expense.date).toLocaleDateString();
    if (key === "dateCreationExpense")
      return expense.dateCreationExpense
        ? new Date(expense.dateCreationExpense).toLocaleDateString()
        : "-";
    if (key === "totalTTC") return `${Number(expense.totalTTC).toFixed(2)} €`;
    if (key === "createdBy") return expense?.createdBy?.name;
    return expense[key];
  };

  const handleEditExpense = (expense) => {
    if (!canEditExpense) return toast.error(t("depense.no_permission"));
    toggleCreateModal(
      <CreateExpenseComponent
        onClose={() => toggleCreateModal(<></>)}
        expense={expense}
        isUpdate={true}
      />,
      t("depense.form.title")
    );
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!canDeleteExpense) return toast.error(t("depense.no_permission"));
    try {
      await deleteExpense({ expenseId, companyId }).unwrap();
      setIsDeleteDialogOpen(false);
      refetch();
      toast.success(t("depense.delete_success"));
    } catch (error) {
      toast.error(t("depense.delete_error"));
    }
  };

  const onSearchExpense = (text) => setSearchText(text.toLowerCase());

  const filteredExpenses = useMemo(() => {
    if (!expenses.length) return [];
    if (searchText.length <= 1) return expenses;

    return expenses.filter((exp) => {
      const invoice = exp.invoiceNumber?.toLowerCase() || "";
      let supplierName = "";
      if (exp?.supplierId?.name) {
        supplierName = exp.supplierId.name;
      } else if (exp?.supplierInfo) {
        try {
          const supplierInfo =
            typeof exp.supplierInfo === "string"
              ? JSON.parse(exp.supplierInfo)
              : exp.supplierInfo;
          supplierName = supplierInfo?.name || "";
        } catch (error) {
          supplierName = "";
        }
      }
      return (
        invoice.includes(searchText) ||
        supplierName.toLowerCase().includes(searchText)
      );
    });
  }, [expenses, searchText]);

  // ✅ Découpage pagination
  const offset = currentPage * itemsPerPage;
  const currentItems = filteredExpenses.slice(offset, offset + itemsPerPage);
  const pageCount = Math.ceil(filteredExpenses.length / itemsPerPage);

  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected);
  };

  if (isLoading) return <Loader />;

  const showActionColumn = canEditExpense || canDeleteExpense;

  return (
    <div className="p-5" style={{ overflowX: "auto" }}>
      <CustomHeader
        title={t("depense.header_title")}
        dataLength={filteredExpenses.length}
        onSearch={onSearchExpense}
      />
      <CustomTable
        headers={headers}
        data={currentItems} // ✅ pagination appliquée
        rowKey="_id"
        renderCell={renderCell}
        actionMenu={
          showActionColumn
            ? (expense) => (
                <>
                  {canEditExpense && (
                    <IconButton
                      aria-label="edit"
                      onClick={() => handleEditExpense(expense)}>
                      <EditIcon color="primary" />
                    </IconButton>
                  )}
                  {canDeleteExpense && (
                    <IconButton
                      aria-label="delete"
                      onClick={() => {
                        setIsDeleteDialogOpen(true);
                        setExpenseToDelete(expense);
                      }}>
                      <DeleteIcon color="error" />
                    </IconButton>
                  )}
                </>
              )
            : undefined
        }
      />
      {/* ✅ Pagination en bas */}
      <div style={{ display: "flex", justifyContent: "center", marginTop: 24 }}>
        <ReactPaginate
          previousLabel={"←"}
          nextLabel={"→"}
          breakLabel={"..."}
          pageCount={pageCount}
          marginPagesDisplayed={1}
          pageRangeDisplayed={2}
          onPageChange={handlePageClick}
          containerClassName={"pagination"}
          activeClassName={"active"}
          forcePage={currentPage}
        />
      </div>

      {isDeleteDialogOpen && (
        <CustomDialog
          open={isDeleteDialogOpen}
          setOpen={setIsDeleteDialogOpen}
          title={t("depense.delete_dialog.title")}
          description={t("depense.delete_dialog.description")}
          cancelText={t("depense.delete_dialog.cancel")}
          submitText={t("depense.delete_dialog.submit")}
          onSubmit={() => handleDeleteExpense(expenseToDelete?._id)}
          isLoading={isDeletingLoading}
        />
      )}
    </div>
  );
};

export default ExpensesPage;
